import type {IssueFieldSingleSelectOption} from '@github-ui/issue-fields-shared/issue-field'
import {IssueSingleSelectFieldPicker} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {AlertIcon} from '@primer/octicons-react'
import {Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentPropsWithoutRef, type Ref, useCallback} from 'react'

import {LABELS} from './constants/labels'
import fieldItemStyles from './IssueFieldItem.module.css'
import {IssueFieldSingleSelectValueToken} from './IssueFieldSingleSelectValueToken'
import styles from './IssueFieldSingleSelectValueToken.module.css'

export type SingleSelectInitialValue = {
  name: string
  color: string
  description?: string
} | null
export type SingleSelectOnCommit = (fieldId: string, value: IssueFieldSingleSelectOption | null) => void

export type IssueFieldSingleSelectEditorProps = {
  fieldId: string
  fieldName: string
  readonly: boolean
  initialValue: SingleSelectInitialValue
  onCommit: SingleSelectOnCommit
  /// If set to true, then the single select editor should render in open state
  shouldOpen?: boolean
  isDraft?: boolean
  className?: string
  errorPresent?: boolean
}

type IssueFieldSingleSelectEditorButtonProps = {
  anchorProps: ComponentPropsWithoutRef<'button'>
  buttonRef: Ref<HTMLButtonElement> | undefined
  ariaLabel: string
  fieldName: string
  displayValue: React.ReactNode
  isNoValue: boolean
  className?: string
  errorPresent: boolean
}

const IssueFieldSingleSelectEditorButton = ({
  anchorProps,
  buttonRef,
  ariaLabel,
  fieldName,
  displayValue,
  isNoValue,
  className,
  errorPresent,
}: IssueFieldSingleSelectEditorButtonProps) => {
  const content = (
    <button
      ref={buttonRef}
      className={clsx(fieldItemStyles.issueFieldFormWrapper, fieldItemStyles.issueFieldFormWrapperClickable, className)}
      aria-label={ariaLabel}
      {...anchorProps}
    >
      <span className={fieldItemStyles.issueFieldLabel}>{fieldName}</span>
      <span
        className={clsx(
          fieldItemStyles.issueFieldValueText,
          !isNoValue && fieldItemStyles.hasValue,
          styles.compactValueContainer,
        )}
      >
        {displayValue}
      </span>
      {errorPresent && (
        <span className={fieldItemStyles.alertIconWrapper}>
          <AlertIcon className={fieldItemStyles.alertIcon} />
        </span>
      )}
    </button>
  )

  return errorPresent ? (
    <Tooltip text={LABELS.errorUpdatingIssueField} type="description">
      {content}
    </Tooltip>
  ) : (
    content
  )
}

export const IssueFieldSingleSelectEditor = ({
  fieldId,
  fieldName,
  readonly,
  initialValue,
  onCommit,
  shouldOpen = false,
  isDraft = false,
  className,
  errorPresent = false,
}: IssueFieldSingleSelectEditorProps) => {
  const onSelectionChanged = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      onCommit(fieldId, selectedOption)
    },
    [fieldId, onCommit],
  )

  const onClose = useCallback(() => {
    if (isDraft) {
      onCommit(fieldId, null)
    }
  }, [fieldId, isDraft, onCommit])

  const isNoValue = !initialValue || (initialValue.name === '' && initialValue.color === '')

  const displayValue = isNoValue ? (
    LABELS.setValue
  ) : (
    <IssueFieldSingleSelectValueToken
      name={initialValue.name}
      color={initialValue.color}
      getTooltipText={() => undefined}
      size="small"
      containerClassName={styles.compactContainer}
    />
  )

  const ariaLabel = isNoValue ? `Set ${fieldName}` : `Edit ${fieldName}`

  if (readonly) {
    return (
      <div className={clsx(fieldItemStyles.issueFieldFormWrapper, className)}>
        <p className={fieldItemStyles.issueFieldLabel}>{fieldName}</p>
        <div
          className={clsx(
            fieldItemStyles.issueFieldValueText,
            !isNoValue && fieldItemStyles.hasValue,
            styles.compactValueContainer,
          )}
        >
          {displayValue}
        </div>
      </div>
    )
  }

  return (
    <IssueSingleSelectFieldPicker
      fieldId={fieldId}
      fieldName={fieldName}
      selectedOption={initialValue ? initialValue.name : null}
      onSelectionChange={onSelectionChanged}
      anchorElement={(anchorProps, ref) => (
        <IssueFieldSingleSelectEditorButton
          anchorProps={anchorProps}
          buttonRef={ref}
          ariaLabel={ariaLabel}
          fieldName={fieldName}
          displayValue={displayValue}
          isNoValue={isNoValue}
          className={className}
          errorPresent={errorPresent}
        />
      )}
      readonly={false}
      isLazy
      onClose={onClose}
      shouldOpen={shouldOpen}
    />
  )
}
